import { InputDateComponent } from '../../../shared/component/input/input-date/input-date.component';
import { FinancialStatement } from '../../models/common/FinancialStatement';
import { Component, Input, EventEmitter, Output, OnDestroy, OnInit, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { FinancialStatementPart } from 'src/app/domain/financial-statement/models/common/FinancialStatementPart';
import { FinancialStatementAccount } from '../../models/common/FinancialStatementAccount';
import { FinancialStatementItem } from '../../models/common/FinancialStatementItem';
import { takeUntil } from 'rxjs/operators';
import {
  PlAllocationEmissionObject,
  baseFinancialStatementItems,
  fixedItemNames,
  AccountFilterData,
  accumulatedProfitAndLoss,
  ItemNameAndStatus,
  FsExcelExportType,
  ManualAccountObjectForEmitter, TOTAL_CHECK_FAILED_TEXT,
} from '../../utils/common/utilityClasses';
import { TrialBalanceComponent } from '../trial-balance/trial-balance.component';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { AssemblyService } from '../../services/assembly/assembly.service';
import { FinancialStatementService } from '../../services/common/financial-statement.service';
import { FsCalculationsService } from '../../services/common/fs-calculations.service';
import { FsExportExcelService } from '../../services/common/fs-export-excel.service';
import {
  ObjectForAccountsModal,
  ObjectForItemDeleteModal,
  ObjectForNameModal,
  ShowBS,
  UnallocatedFSData,
} from '../../utils/assembly/utils';
import { FsSelectorPeriod } from '../../utils/common/FsSelectorPeriod';
import { Router } from '@angular/router';
import { FS_URLS } from '../../module/financial-statement-routing.module';
import { FsGettersSettersService } from '../../services/common/fs-getters-setters.service';

@Component({
  selector: 'app-assembly',
  templateUrl: './assembly.component.html',
  styleUrls: ['./assembly.component.scss'],
  providers: [AssemblyService],
})
export class AssemblyComponent implements OnInit, OnDestroy, OnChanges {

  protected _destroyStream: Subject<void> = new Subject<void>();
  private keyForModal = 'financialStatementAccounts'; // key value for multi-select-autocomplete shared modal

  private selectedPart: FinancialStatementPart; // stores index on this.parts of which part is being edited
  selectedItem: FinancialStatementItem; // stores index in this.parts[this.selectedPart] of which item is being edited
  isAllocating = false;

  @Input() isAdmin: boolean;
  @Input() isPL: boolean;
  @Input() financialStatement: FinancialStatement;
  @Input() set isAccountsDrilldown(showAccounts: boolean) {
    this.show.accounts = showAccounts;
  }

  @Output() plAllocationEmitter: EventEmitter<PlAllocationEmissionObject> = new EventEmitter<PlAllocationEmissionObject>();
  @Output() isDrillDownEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() scrollChangeEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() manualEntryModalEmitter: EventEmitter<void> = new EventEmitter<void>(); // add onclick
  @Output() manualAccountEmitter: EventEmitter<ManualAccountObjectForEmitter> = new EventEmitter<ManualAccountObjectForEmitter>();
  @Output() threeDotMenuEmitter: EventEmitter<'copyStruct' | 'clearStruct'> = new EventEmitter<'copyStruct' | 'clearStruct'>();
  @Output() bulkDeleteAccountsEmitter: EventEmitter<FinancialStatementAccount[]> = new EventEmitter<FinancialStatementAccount[]>();
  @ViewChild('itemDrilldown') itemDrilldown: TrialBalanceComponent;
  @ViewChild(TrialBalanceComponent) trialBalanceComponent: TrialBalanceComponent;
  @ViewChild('accountFilterForDataDatePicker') accountFilterForDataDatePicker: InputDateComponent;

  // objects to pass to account modals, needed to provide further access in modals
  objectForAccountsModal: ObjectForAccountsModal;
  objectForItemModal: ObjectForNameModal;
  objectForItemDeleteModal: ObjectForItemDeleteModal;
  unallocatedFSData: UnallocatedFSData = this.assemblyService.initUnallocatedFSData();
  // base for balance sheet
  parts: FinancialStatementPart[] = undefined;
  show = new ShowBS();
  switcher = 1;
  baseFinancialStatementItems = baseFinancialStatementItems;
  financialStatementPeriod: Date;
  selectedItemForDrilldown: FinancialStatementItem;
  accountFilterDataForDrilldown: AccountFilterData;

  protected readonly fixedItemNames = fixedItemNames;
  protected readonly accumulatedProfitAndLossItemName = accumulatedProfitAndLoss;
  protected readonly TOTAL_CHECK_FAILED_TEXT = TOTAL_CHECK_FAILED_TEXT;

  constructor(
    protected financialStatementService: FinancialStatementService,
    protected fsExcelService: FsExportExcelService,
    protected router: Router,
    private assemblyService: AssemblyService,
    private fsGetterSetter: FsGettersSettersService,
  ) { }

  ngOnInit(): void {
    this.initComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.financialStatement) {
      this.onChange();
      this.calculateTotalsAndSums();
    }
  }

  protected onInitSubscribe(financialStatementPeriod: FsSelectorPeriod): void {
    this.financialStatementPeriod = financialStatementPeriod.period;
  }

  protected onChange(): void {
    if (!this.financialStatement) {
      return;
    }
    this.parts = this.isPL ? this.financialStatement.profitLossParts : this.financialStatement.balanceSheetParts;
    this.updateUnallocatedAccounts();
    this.assemblyService.removeAllSelectedFromUnallocated(this.parts, this.unallocatedFSData);
  }


  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  // triggered when accounts allocation modal is confirmed
  confirmAccountSelection(accounts: FinancialStatementAccount[]): void {
    if (accounts) {
      this.editAccounts(accounts, this.selectedItem);
    }
    this.show.modalForAddAccounts = false;
    this.scrollChangeEmitter.emit(false);
    this.isAllocating = false;
  }

  // triggers open/close of part based on index in this.parts. first two, checks if PL or BS, third always BS
  closePart(partName: string): void {
    this.show.parts[partName] = !this.show.parts[partName];
  }

  // triggered when allocate accounts clicked, accepts and sets the index of the part and item being edited,
  // loads object for modal with updated data and key for modal
  selectAccountsToAdd(part: FinancialStatementPart, item: FinancialStatementItem): void {
    if (this.financialStatement.isCompleted || !this.isAdmin) {
      return;
    }
    this.isAllocating = true;
    this.scrollChangeEmitter.emit(true);
    this.selectedPart = part;
    this.selectedItem = item;
    this.objectForAccountsModal = {
      unallocated: this.unallocatedFSData.accounts,
      allocated: item.financialStatementAccounts,
      key: this.keyForModal,
      itemName: item.name,
    };
    this.show.modalForAddAccounts = true;
  }

  removeItem(part: FinancialStatementPart, itemToRemove: FinancialStatementItem): void {
    this.selectedPart = part; // have to set this for editAccounts method
    this.editAccounts([], itemToRemove);
    // handle removal and order adjustment for part's items + update in db
    const lastOrder = this.selectedPart.financialStatementItems.length - 1;
    this.selectedPart.financialStatementItems = this.selectedPart.financialStatementItems.filter(
      (item) => item.id !== itemToRemove.id,
    );
    this.selectedPart.financialStatementItems = this.assemblyService.adjustOrderNumbersInRange(
      this.selectedPart.financialStatementItems,
      itemToRemove.orderNumber,
      lastOrder,
      -1,
    );
    // substitute with updated part
    this.financialStatement.items = this.financialStatement.items
      .filter((item) => item.partName !== part.partName)
      .concat(this.selectedPart.financialStatementItems);

    this.financialStatementService.deleteItem(itemToRemove);
    this.calculateTotalsAndSums();
  }

  // triggers save with name modal
  nameItem(part: FinancialStatementPart, item?: FinancialStatementItem): void {
    this.selectedPart = part; // sets part being edited
    this.show.modalForAddItem = true;
    if (item) {
      this.selectedItem = item; // sets item being edited
    }

    this.objectForItemModal = this.assemblyService.initObjectForItemModal(this.isPL, this.financialStatement.items, part, item);
  }

  // triggered when name modal is confirmed
  async confirmName(itemNameAndStatus: ItemNameAndStatus): Promise<void> {
    if (itemNameAndStatus) {
      if (this.objectForItemModal.isNew) {
        await this.assemblyService.storeNewItem(itemNameAndStatus, this.selectedPart, this.financialStatement);
      } else {
        this.assemblyService.updateItem(itemNameAndStatus, this.selectedItem);
      }
      this.calculateTotalsAndSums();
    }
    this.show.modalForAddItem = false;
  }

  manualEntryModalEmitterCatcher(): void {
    this.manualEntryModalEmitter.emit();
  }

  showAllocatedAccounts(item: FinancialStatementItem): void {
    if (!item.financialStatementAccounts.length) {
      return;
    }
    this.drilldownDataSetup(item);
    this.isDrillDownEmitter.emit(true);
  }

  drilldownFilterChange(selectedPeriod: Date, selectedDate: Date): void {
    this.accountFilterDataForDrilldown.selectedPeriod = selectedPeriod;
    this.accountFilterForDataDatePicker.maxDate = String(this.accountFilterDataForDrilldown.selectedPeriod);
    if (this.accountFilterForDataDatePicker.input > this.accountFilterForDataDatePicker.maxDate) {
      this.accountFilterForDataDatePicker.input = this.accountFilterForDataDatePicker.maxDate;
    }
    this.accountFilterDataForDrilldown.selectedDate = selectedDate;
    this.itemDrilldown.accountFilterDataObject = new AccountFilterData(this.accountFilterDataForDrilldown);
    this.itemDrilldown.dataSetup();
  }

  resetAccountFiltersForDrilldown(): void {
    this.accountFilterDataForDrilldown.selectedPeriod = this.financialStatement.period;
    this.accountFilterDataForDrilldown.selectedDate = null;
    this.itemDrilldown.accountFilterDataObject = new AccountFilterData(this.accountFilterDataForDrilldown);
    this.itemDrilldown.dataSetup();
  }

  /**
   Updates the list of unallocated accounts in the financial statement data.
   This function should be called whenever an account is created, edited, allocated, or unallocated.
   Sorting the unallocated accounts maintains a consistent and predictable order by account code,
   particularly when accounts are edited or new accounts are created.
   */
  updateUnallocatedAccounts(): void {
    const filteredAccounts = this.financialStatementService.financialStatement?.tb?.filter((a) => !a.financialStatementItems.length)
      .slice() ?? [];
    this.unallocatedFSData.accounts =
      this.assemblyService.filterPLAccounts(filteredAccounts, this.isPL).sort((a, b) => a.code.localeCompare(b.code));
  }

  showAndSetItemDeleteModal(part: FinancialStatementPart, itemToRemove: FinancialStatementItem): void {
    this.objectForItemDeleteModal = {
      itemToBeRemoved: itemToRemove,
      parentPart: part,
      description: itemToRemove.name.toUpperCase() + ' will be deleted',
    };
    this.show.thinkTwiceModalItemDelete = true;
  }

  confirmItemDelete(isConfirmed: boolean): void {
    if (isConfirmed) {
      this.removeItem(this.objectForItemDeleteModal.parentPart, this.objectForItemDeleteModal.itemToBeRemoved);
    }
    this.objectForItemDeleteModal = null;
    this.show.thinkTwiceModalItemDelete = false;
  }

  /** processes changes to selected and unselected accounts called after allocation modal confirmation */
  editAccounts(selectedAccounts: FinancialStatementAccount[], item: FinancialStatementItem): void {
    this.assemblyService.handleAccountEdit(selectedAccounts, item, this.unallocatedFSData);
    this.calculateTotalsAndSums();
    this.financialStatementService.setAccountItems(this.financialStatement.tb, this.financialStatement.items);

    if (this.isPL) {
      selectedAccounts = this.financialStatement.tb.filter((acc) =>
        acc.isSelected && (acc.code.startsWith('4') || acc.code.startsWith('5')));
      this.plAllocationEmitter.emit({ selectedAccounts, item });
    }
  }

  calculateTotalsAndSums(): void {
    if (!this.financialStatement) {
      return;
    }
    FsCalculationsService.calculateBalanceSheetTotals(
      this.isPL ? this.financialStatement.profitLossNetTotals : this.financialStatement.balanceSheetNetTotals,
      this.parts,
      this.isPL,
    );
    FsCalculationsService.calculateUnallocatedSums(this.unallocatedFSData.sums, this.unallocatedFSData.accounts);
    this.assemblyService.updateUnallocatedChecks(this.isPL, this.unallocatedFSData);
  }

  drop(event: CdkDragDrop<string[]>, part: FinancialStatementPart): void {
    if (!this.isAdmin || this.financialStatement.isCompleted) {
      return;
    }
    this.assemblyService.handleDragAndDrop(event, part);
  }

  exportExcel(): void {
    this.fsExcelService.exportExcel(FsExcelExportType.ASSEMBLY);
  }

  goToAssembly(): void {
    void this.router.navigate([FS_URLS.base], { queryParamsHandling: 'preserve' });
  }

  private initComponent(): void {
    this.fsGetterSetter.selectedEntityPeriod
      .pipe(takeUntil(this._destroyStream))
      .subscribe((financialStatementPeriod) => {
        if (!financialStatementPeriod) {
          return;
        }
        this.onInitSubscribe(financialStatementPeriod);
        this.calculateTotalsAndSums();
      });
  }

  private drilldownDataSetup(item: FinancialStatementItem): void {
    this.selectedItemForDrilldown = item;
    this.show.accounts = true;
    this.accountFilterDataForDrilldown = {
      selectorPeriods: this.fsGetterSetter.ownershipEntityPeriods.value.map((period) => period.period),
      selectedPeriod: Object.assign(this.financialStatement.period),
      selectedDate: null,
    };
  }
}
